import type { BusinessListing, WebsiteSectionId } from '../types';

/** Body sections in their default order (navbar + footer are always rendered outside this list). */
const SECTIONS: readonly WebsiteSectionId[] = [
  'hero',
  'trust',
  'services',
  'about',
  'reviews',
  'booking_cta',
  'faq',
  'location',
];

/** Spacing between default-ordered sections so explicit integers can slot between. */
const DEFAULT_ORDER_STEP = 100;

function defaultSlotOrder(id: WebsiteSectionId): number {
  return SECTIONS.indexOf(id) * DEFAULT_ORDER_STEP;
}

function parseSectionsValue(raw: unknown): 'visible_default' | 'hidden' | { kind: 'order'; n: number } {
  if (raw === false || raw === null) return 'hidden';
  if (raw === true) return 'visible_default';

  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return { kind: 'order', n: raw };
  }

  if (typeof raw === 'string') {
    const t = raw.trim().toLowerCase();
    if (t === 'false' || t === '') return 'hidden';
    if (t === 'true') return 'visible_default';
    const n = Number(raw);
    if (Number.isFinite(n)) return { kind: 'order', n };
  }

  return 'hidden';
}

function sectionsConfigIsActive(sections: unknown): sections is Record<string, unknown> {
  if (sections == null || typeof sections !== 'object' || Array.isArray(sections)) return false;
  return Object.keys(sections as object).length > 0;
}

/**
 * Ordered body sections between navbar and footer from Firestore `sections`.
 *
 * - No `sections`, null, non-object, or `{}`: full default order, all visible.
 * - Non-empty `sections`: only keys set to `true` or a finite number are shown (`true` uses
 *   the default slot for sorting). Missing keys, `false`, `null`, or invalid values hide.
 * - Keys other than body section ids (e.g. legacy `navbar`) are ignored.
 * - Legacy `contact` is treated as `location` if `location` is not set.
 */
export function getOrderedSections(sections: BusinessListing['sections']): WebsiteSectionId[] {
  const order = SECTIONS;

  if (!sectionsConfigIsActive(sections)) {
    return [...order];
  }

  const rawMap = sections as Record<string, unknown>;

  function rawForSection(id: WebsiteSectionId): unknown {
    if (Object.prototype.hasOwnProperty.call(sections, id)) {
      return rawMap[id];
    }
    if (id === 'location' && Object.prototype.hasOwnProperty.call(rawMap, 'contact')) {
      return rawMap['contact'];
    }
    return undefined;
  }

  const rows: { id: WebsiteSectionId; sort: number }[] = [];

  for (const id of order) {
    const hasEntry =
      Object.prototype.hasOwnProperty.call(rawMap, id) ||
      (id === 'location' && Object.prototype.hasOwnProperty.call(rawMap, 'contact'));
    if (!hasEntry) continue;

    const raw = rawForSection(id);
    const parsed = parseSectionsValue(raw);
    if (parsed === 'hidden') continue;
    if (parsed === 'visible_default') {
      rows.push({ id, sort: defaultSlotOrder(id) });
      continue;
    }
    rows.push({ id, sort: parsed.n });
  }

  rows.sort((a, b) => {
    if (a.sort !== b.sort) return a.sort - b.sort;
    return order.indexOf(a.id) - order.indexOf(b.id);
  });

  return rows.map(r => r.id);
}
